const preventDefault = (event) => {
  event.preventDefault();
  return false;
};

class CustomSlider extends HTMLElement {
  constructor() {
    super();

    this.currentPosition = 0;
    this.isDragging = false;
    this.startX = 0;
    this.startPosition = 0;
    this.autoRotateTimer = null;
    this.autoRotateInterval = 4000; // Default 4 seconds

    this.handleDrag = this.handleDrag.bind(this);
    this.startDrag = this.startDrag.bind(this);
    this.endDrag = this.endDrag.bind(this);
    this.calculateBounds = this.calculateBounds.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handlePrev = this.handlePrev.bind(this);
    this.updateCounter = this.updateCounter.bind(this);
    this.startAutoRotate = this.startAutoRotate.bind(this);
    this.stopAutoRotate = this.stopAutoRotate.bind(this);
    this.handleAutoRotate = this.handleAutoRotate.bind(this);
    this.debouncedBounds = debounce(this.calculateBounds, 100);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.track =
      this.querySelector("[data-slider-track]") || this.firstElementChild;
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll("[data-slider-item]"));
    if (this.slides.length < 3) return;

    // Check for infinite scroll option
    this.isInfinite = this.getAttribute("data-infinite") === "true";
    this.realSlideCount = this.slides.length;
    this.firstCloneIndex = null;
    this.lastCloneIndex = null;

    // Check for auto-rotate option
    this.isAutoRotate = this.getAttribute("data-auto-rotate") === "true";
    if (this.isAutoRotate) {
      const rotationSpeed = parseInt(
        this.getAttribute("data-rotation-speed") || "4",
        10
      );
      this.autoRotateInterval = rotationSpeed * 1000; // Convert seconds to milliseconds
    }

    // Clone slides for infinite scroll if enabled
    if (this.isInfinite && this.realSlideCount >= 2) {
      this.setupInfiniteSlides();
    }

    // Re-query slides after cloning (if infinite was enabled)
    this.slides = Array.from(this.track.querySelectorAll("[data-slider-item]"));

    this.clickThreshold = parseInt(
      this.getAttribute("click-threshold") || "5",
      10
    );
    this.dragThreshold = parseInt(
      this.getAttribute("drag-threshold") || "30",
      10
    );

    this.querySelectorAll("img").forEach((img) => {
      img.setAttribute("draggable", "false");
      img.addEventListener("dragstart", preventDefault, { passive: false });
    });

    this.calculateBounds();

    window.addEventListener("resize", this.debouncedBounds);
    window.addEventListener("load", this.calculateBounds);

    this.track.addEventListener("mousedown", this.startDrag);
    this.track.addEventListener("touchstart", this.startDrag, {
      passive: false,
    });

    this.nextButton = this.querySelector("[data-slider-next]");
    this.prevButton = this.querySelector("[data-slider-prev]");
    this.counterCurrent = this.querySelector("[data-slider-counter-current]");
    this.counterTotal = this.querySelector("[data-slider-counter-total]");

    if (this.nextButton) {
      this.nextButton.addEventListener("click", this.handleNext);
    }

    if (this.prevButton) {
      this.prevButton.addEventListener("click", this.handlePrev);
    }

    this.toggleButtonState();
    this.updateCounter();

    // Start auto-rotation if enabled
    if (this.isAutoRotate && this.slides.length >= 2) {
      this.startAutoRotate();
    }

    this.initialized = true;
  }

  setupInfiniteSlides() {
    if (this.realSlideCount < 2) return;

    const firstSlide = this.slides[0];
    const lastSlide = this.slides[this.realSlideCount - 1];

    // Clone last slide and insert at the beginning
    const lastClone = lastSlide.cloneNode(true);
    lastClone.setAttribute("data-slider-item", "");
    lastClone.setAttribute("data-infinite-clone", "first");
    this.track.insertBefore(lastClone, firstSlide);

    // Clone first slide and append at the end
    const firstClone = firstSlide.cloneNode(true);
    firstClone.setAttribute("data-slider-item", "");
    firstClone.setAttribute("data-infinite-clone", "last");
    this.track.appendChild(firstClone);

    // Update indices
    this.firstCloneIndex = 0; // First clone is now at index 0
    this.lastCloneIndex = this.realSlideCount + 1; // Last clone is at the end
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.stopAutoRotate();

    window.removeEventListener("resize", this.debouncedBounds);
    window.removeEventListener("load", this.calculateBounds);

    this.track.removeEventListener("mousedown", this.startDrag);
    this.track.removeEventListener("touchstart", this.startDrag);

    document.removeEventListener("mousemove", this.handleDrag);
    document.removeEventListener("touchmove", this.handleDrag);
    document.removeEventListener("mouseup", this.endDrag);
    document.removeEventListener("touchend", this.endDrag);

    if (this.nextButton) {
      this.nextButton.removeEventListener("click", this.handleNext);
    }

    if (this.prevButton) {
      this.prevButton.removeEventListener("click", this.handlePrev);
    }

    this.initialized = false;
  }

  startDrag(event) {
    if (event.type === "mousedown" && event.button !== 0) return;

    // Stop auto-rotation when dragging starts
    this.stopAutoRotate();

    this.isDragging = false;
    this.startX =
      event.type === "touchstart" ? event.touches[0].clientX : event.clientX;
    this.startPosition = this.currentPosition;

    document.addEventListener("mousemove", this.handleDrag);
    document.addEventListener("touchmove", this.handleDrag, { passive: false });
    document.addEventListener("mouseup", this.endDrag);
    document.addEventListener("touchend", this.endDrag);

    event.preventDefault();
  }

  handleDrag(event) {
    const currentX =
      event.type === "touchmove" ? event.touches[0].clientX : event.clientX;
    const diff = this.startX - currentX;

    if (!this.isDragging && Math.abs(diff) < this.clickThreshold) return;

    if (!this.isDragging) {
      this.isDragging = true;
      this.track.classList.add("dragging");
      document.body.classList.add("slider-dragging");
      this.track.style.transition = "none";
    }

    let nextPosition = this.startPosition - diff;

    // For infinite mode, allow dragging beyond bounds temporarily
    if (this.isInfinite) {
      this.currentPosition = nextPosition;
    } else {
      nextPosition = Math.max(Math.min(nextPosition, 0), -this.maxPosition);
      this.currentPosition = nextPosition;
    }

    this.track.style.transform = `translateX(${nextPosition}px)`;

    event.preventDefault();
  }

  endDrag(event) {
    document.removeEventListener("mousemove", this.handleDrag);
    document.removeEventListener("touchmove", this.handleDrag);
    document.removeEventListener("mouseup", this.endDrag);
    document.removeEventListener("touchend", this.endDrag);

    document.body.classList.remove("slider-dragging");

    if (!this.isDragging) {
      const target = event.target.closest("a");
      if (target) target.click();
      return;
    }

    this.isDragging = false;
    this.track.classList.remove("dragging");

    const movement = this.currentPosition - this.startPosition;

    if (this.cardWidth > 0) {
      if (movement <= -this.dragThreshold) {
        // Moving to next slide - calculate exact position
        const currentIndex = Math.round(
          Math.abs(this.currentPosition) / this.cardWidth
        );
        const nextPosition = -(currentIndex + 1) * this.cardWidth;
        if (this.isInfinite) {
          this.slideToInfinite(nextPosition, "next");
        } else {
          this.currentPosition = Math.max(nextPosition, -this.maxPosition);
          this.slideTo(this.currentPosition);
        }
      } else if (movement >= this.dragThreshold) {
        // Moving to previous slide - calculate exact position
        const currentIndex = Math.round(
          Math.abs(this.currentPosition) / this.cardWidth
        );
        const prevPosition = -Math.max(0, currentIndex - 1) * this.cardWidth;
        if (this.isInfinite) {
          this.slideToInfinite(prevPosition, "prev");
        } else {
          this.currentPosition = Math.min(prevPosition, 0);
          this.slideTo(this.currentPosition);
        }
      } else {
        // Snap back to nearest slide
        if (this.isInfinite && this.cardWidth > 0) {
          const currentIndex = Math.round(
            Math.abs(this.currentPosition) / this.cardWidth
          );
          const snapPosition = -currentIndex * this.cardWidth;
          this.slideToInfinite(snapPosition, "snap");
        } else {
          const currentIndex = Math.round(
            Math.abs(this.currentPosition) / this.cardWidth
          );
          const snapPosition = -currentIndex * this.cardWidth;
          this.currentPosition = Math.max(
            Math.min(snapPosition, 0),
            -this.maxPosition
          );
          this.slideTo(this.currentPosition);
        }
      }
    } else {
      this.currentPosition = Math.max(
        Math.min(this.currentPosition, 0),
        -this.maxPosition
      );
      this.slideTo(this.currentPosition);
    }

    this.toggleButtonState();
    this.updateCounter();

    // Restart auto-rotation after drag ends
    if (this.isAutoRotate && this.slides.length >= 2) {
      this.startAutoRotate();
    }
  }

  slideTo(position) {
    this.currentPosition = position;
    this.track.style.transition = "transform 0.3s ease";
    this.track.style.transform = `translateX(${position}px)`;

    // For infinite mode, check after transition completes to catch any edge cases
    if (this.isInfinite) {
      const handleTransitionEnd = () => {
        this.track.removeEventListener("transitionend", handleTransitionEnd);
        this.checkAndFixInfinitePosition();
      };
      this.track.addEventListener("transitionend", handleTransitionEnd, {
        once: true,
      });
    }

    window.requestAnimationFrame(() => {
      this.toggleButtonState();
      this.updateCounter();
    });
  }

  checkAndFixInfinitePosition() {
    if (!this.isInfinite || this.cardWidth <= 0) return;

    const currentIndex = Math.round(
      Math.abs(this.currentPosition) / this.cardWidth
    );

    // Check if we ended up at a fake slide after transition completes
    if (currentIndex >= this.lastCloneIndex) {
      // We've reached the fake last slide (cloned first at the end)
      // Jump instantly to real first slide (index 1, after cloned last slide)
      this.currentPosition = -this.cardWidth;
      this.track.style.transition = "none";
      this.track.style.transform = `translateX(${this.currentPosition}px)`;
      // Use double requestAnimationFrame to ensure the jump happens after the previous transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.track.style.transition = "transform 0.3s ease";
          this.toggleButtonState();
          this.updateCounter();
        });
      });
    } else if (
      currentIndex <= this.firstCloneIndex &&
      this.currentPosition >= 0
    ) {
      // We've reached the fake first slide (cloned last at index 0)
      // Jump instantly to real last slide
      const realLastIndex = this.realSlideCount;
      this.currentPosition = -(realLastIndex * this.cardWidth);
      this.track.style.transition = "none";
      this.track.style.transform = `translateX(${this.currentPosition}px)`;
      // Use double requestAnimationFrame to ensure the jump happens after the previous transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.track.style.transition = "transform 0.3s ease";
          this.toggleButtonState();
          this.updateCounter();
        });
      });
    }
  }

  handleNext(event) {
    if (event) {
      event.preventDefault();
    }
    if (this.cardWidth <= 0) return;

    // Reset auto-rotation timer when manually navigating
    if (this.isAutoRotate) {
      this.stopAutoRotate();
      this.startAutoRotate();
    }

    // Calculate exact next position based on current index
    const currentIndex = Math.round(
      Math.abs(this.currentPosition) / this.cardWidth
    );
    const nextPosition = -(currentIndex + 1) * this.cardWidth;

    if (this.isInfinite) {
      this.slideToInfinite(nextPosition, "next");
    } else {
      const finalPosition = Math.max(nextPosition, -this.maxPosition);
      this.slideTo(finalPosition);
      this.toggleButtonState();
    }
  }

  handlePrev(event) {
    if (event) {
      event.preventDefault();
    }
    if (this.cardWidth <= 0) return;

    // Reset auto-rotation timer when manually navigating
    if (this.isAutoRotate) {
      this.stopAutoRotate();
      this.startAutoRotate();
    }

    // Calculate exact previous position based on current index
    const currentIndex = Math.round(
      Math.abs(this.currentPosition) / this.cardWidth
    );
    const prevPosition = -Math.max(0, currentIndex - 1) * this.cardWidth;

    if (this.isInfinite) {
      this.slideToInfinite(prevPosition, "prev");
    } else {
      const finalPosition = Math.min(prevPosition, 0);
      this.slideTo(finalPosition);
      this.toggleButtonState();
    }
  }

  slideToInfinite(position, direction) {
    if (this.cardWidth <= 0) return;

    // Allow the transition to complete, then check if we need to jump
    // This creates a seamless infinite scroll effect
    this.currentPosition = position;
    this.slideTo(position);
  }

  toggleButtonState() {
    // For infinite scroll, buttons are never disabled
    if (this.isInfinite) {
      if (this.prevButton) {
        this.prevButton.disabled = false;
        this.prevButton.classList.remove("is-disabled");
      }
      if (this.nextButton) {
        this.nextButton.disabled = false;
        this.nextButton.classList.remove("is-disabled");
      }
      return;
    }

    const atStart = Math.abs(this.currentPosition) < 1;
    const atEnd = Math.abs(this.currentPosition + this.maxPosition) < 1;

    if (this.prevButton) {
      this.prevButton.disabled = atStart;
      this.prevButton.classList.toggle("is-disabled", atStart);
    }

    if (this.nextButton) {
      this.nextButton.disabled = atEnd;
      this.nextButton.classList.toggle("is-disabled", atEnd);
    }
  }

  calculateBounds() {
    const containerWidth = this.getBoundingClientRect().width;
    const trackStyles = window.getComputedStyle(this.track);

    const gapValue = parseFloat(
      trackStyles.columnGap || trackStyles.gap || "0"
    );
    const paddingLeft = parseFloat(trackStyles.paddingLeft || "0");
    const paddingRight = parseFloat(trackStyles.paddingRight || "0");

    this.gap = isNaN(gapValue) ? 0 : gapValue;

    // Calculate card width more accurately
    const firstSlideRect = this.slides[0].getBoundingClientRect();
    const secondSlideRect =
      this.slides.length > 1 ? this.slides[1].getBoundingClientRect() : null;

    if (secondSlideRect && this.slides.length > 1) {
      // Use the distance between first and second slide for more accurate width calculation
      this.cardWidth = Math.abs(secondSlideRect.left - firstSlideRect.left);
    } else {
      // Fallback to width + gap if only one slide or calculation fails
      this.cardWidth = firstSlideRect.width + this.gap;
    }

    const trackWidth =
      this.cardWidth * this.slides.length +
      paddingLeft +
      paddingRight -
      this.gap;
    this.maxPosition = Math.max(trackWidth - containerWidth, 0);

    // For infinite scroll, preserve current real slide index or start at real index 0
    if (
      this.isInfinite &&
      this.firstCloneIndex !== null &&
      this.cardWidth > 0
    ) {
      // If we have a valid current position, preserve the real slide index
      if (
        this.currentPosition !== undefined &&
        Math.abs(this.currentPosition) > 0
      ) {
        const currentIndex = Math.round(
          Math.abs(this.currentPosition) / this.cardWidth
        );
        // Map to real slide index
        let realIndex = 0;
        if (currentIndex === this.firstCloneIndex) {
          realIndex = this.realSlideCount - 1;
        } else if (currentIndex >= this.lastCloneIndex) {
          realIndex = 0;
        } else if (currentIndex > 0) {
          realIndex = currentIndex - 1;
          realIndex = Math.max(0, Math.min(realIndex, this.realSlideCount - 1));
        }
        // Position at the corresponding real slide (realIndex + 1 because of clone at start)
        this.currentPosition = -(realIndex + 1) * this.cardWidth;
      } else {
        // Initial load: Position at the first real slide (index 1, after the cloned last slide)
        this.currentPosition = -this.cardWidth;
      }
    } else {
      this.currentPosition = Math.max(
        Math.min(this.currentPosition, 0),
        -this.maxPosition
      );
    }

    const previousTransition = this.track.style.transition;
    this.track.style.transition = "none";
    this.track.style.transform = `translateX(${this.currentPosition}px)`;
    requestAnimationFrame(() => {
      this.track.style.transition = previousTransition;
      this.toggleButtonState();
      this.updateCounter();
    });

    this.updateCounter();
  }

  updateCounter() {
    if (!this.counterCurrent && !this.counterTotal) return;

    // For infinite scroll, use real slide count; otherwise use total slides
    const totalSlides = this.isInfinite
      ? this.realSlideCount
      : this.slides.length;
    if (this.counterTotal) {
      this.counterTotal.textContent = String(totalSlides).padStart(2, "0");
    }

    if (totalSlides === 0 || !this.counterCurrent) return;

    let currentIndex = 0;
    if (this.cardWidth > 0) {
      currentIndex = Math.round(
        Math.abs(this.currentPosition) / this.cardWidth
      );
    }

    // For infinite scroll, map to real slide index
    if (
      this.isInfinite &&
      this.firstCloneIndex !== null &&
      this.lastCloneIndex !== null
    ) {
      // Map fake indices to real indices
      if (currentIndex === this.firstCloneIndex) {
        // Fake first (cloned last) = real last slide
        currentIndex = this.realSlideCount - 1;
      } else if (currentIndex >= this.lastCloneIndex) {
        // Fake last (cloned first) = real first slide
        currentIndex = 0;
      } else {
        // Real slides are from index 1 to realSlideCount
        // Map: index 1 -> real slide 0, index 2 -> real slide 1, etc.
        currentIndex = currentIndex - 1;
        // Ensure it's within bounds
        currentIndex = Math.max(
          0,
          Math.min(currentIndex, this.realSlideCount - 1)
        );
      }
    } else {
      currentIndex = Math.min(
        Math.max(currentIndex, 0),
        Math.max(totalSlides - 1, 0)
      );
    }

    this.counterCurrent.textContent = String(currentIndex + 1).padStart(2, "0");
  }

  startAutoRotate() {
    if (!this.isAutoRotate || this.slides.length < 2) return;
    this.stopAutoRotate(); // Clear any existing timer
    this.autoRotateTimer = window.setTimeout(
      this.handleAutoRotate,
      this.autoRotateInterval
    );
  }

  stopAutoRotate() {
    if (this.autoRotateTimer) {
      window.clearTimeout(this.autoRotateTimer);
      this.autoRotateTimer = null;
    }
  }

  handleAutoRotate() {
    if (!this.isAutoRotate || this.slides.length < 2) return;
    // Automatically move to next slide
    this.handleNext(null);
  }
}

if (!customElements.get("custom-slider")) {
  customElements.define("custom-slider", CustomSlider);
}

class HomeHero {
  constructor(root) {
    this.root = root;
    this.header = document.getElementById("main-header");
    this.backgroundImage = root.querySelector("[data-hero-background-image]");
    this.background = root.querySelector("[data-hero-background]");
    this.button = root.querySelector("[data-hero-button]");
    this.thumbnails = Array.from(
      root.querySelectorAll("[data-hero-thumbnail]")
    );
    this.activeIndex = this.thumbnails.findIndex((thumb) =>
      thumb.classList.contains("is-active")
    );
    this.currentSlide = this.getCurrentSlide();
    this.isAnimating = false;
    this.sceneTimeline = null;
    this.loadTimeline = null;
    this.gsap = window.gsap || null;
    this.autoSlideTimer = null;
    this.autoSlideInterval = parseInt(
      this.root.dataset.heroAutoInterval || "5000",
      10
    );
    if (
      !Number.isFinite(this.autoSlideInterval) ||
      this.autoSlideInterval <= 0
    ) {
      this.autoSlideInterval = 0;
    }
    this.autoSlideEnabled =
      this.autoSlideInterval > 0 && this.thumbnails.length > 1;
    this.currentThumbnailIndex = this.activeIndex >= 0 ? this.activeIndex : 0;

    this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
    this.handleAutoSlide = this.handleAutoSlide.bind(this);
    this.startAutoSlide = this.startAutoSlide.bind(this);
    this.resetAutoSlideTimer = this.resetAutoSlideTimer.bind(this);
    this.clearAutoSlideTimer = this.clearAutoSlideTimer.bind(this);
    this.setInteractionEnabled = this.setInteractionEnabled.bind(this);
    this.handleResize = this.handleResize.bind(this);

    // Allow initialization if we have background image and button, even without thumbnails
    // (e.g., when there's only 1 block, thumbnails might be empty)
    if (!this.backgroundImage || !this.button) {
      return;
    }

    if (this.gsap) {
      this.init();
      return;
    }

    this.waitForGsap()
      .then((lib) => {
        this.gsap = lib;
        this.init();
      })
      .catch(() => {
        this.setStaticState();
      });
  }

  getImagesWithFallback() {
    let desktopImage = this.backgroundImage.dataset.imageDesktop || "";
    let mobileImage = this.backgroundImage.dataset.imageMobile || "";

    // Bidirectional fallback: if one is missing, use the other
    if (!desktopImage && mobileImage) desktopImage = mobileImage;
    if (!mobileImage && desktopImage) mobileImage = desktopImage;

    return { desktopImage, mobileImage };
  }

  getCurrentSlide() {
    const isMobile = window.innerWidth < 750;
    const { desktopImage, mobileImage } = this.getImagesWithFallback();

    // Read collection URL from data attribute (try both dataset and getAttribute)
    let collectionUrl =
      this.backgroundImage.dataset.activeCollectionUrl ||
      this.backgroundImage.getAttribute("data-active-collection-url");

    // Fallback to button href if dataset is empty or "#"
    if (!collectionUrl || collectionUrl === "#") {
      const buttonHref = this.button.getAttribute("href");
      if (buttonHref && buttonHref !== "#") {
        collectionUrl = buttonHref;
      }
    }

    return {
      image: isMobile ? mobileImage : desktopImage,
      imageDesktop: desktopImage,
      imageMobile: mobileImage,
      thumb: this.backgroundImage.dataset.activeThumbSrc || "",
      thumbSrcset: this.backgroundImage.dataset.activeThumbSrcset || "",
      collectionUrl: collectionUrl || "#",
      alt: this.backgroundImage.getAttribute("alt") || "",
    };
  }

  getImageForCurrentScreen() {
    const isMobile = window.innerWidth < 750;
    const { desktopImage, mobileImage } = this.getImagesWithFallback();
    return isMobile ? mobileImage : desktopImage;
  }

  waitForGsap(maxAttempts = 12, delay = 120) {
    return new Promise((resolve, reject) => {
      let attempts = 0;

      const tryResolve = () => {
        if (window.gsap) {
          resolve(window.gsap);
          return;
        }

        attempts += 1;

        if (attempts >= maxAttempts) {
          reject(new Error("GSAP not available"));
          return;
        }

        setTimeout(tryResolve, delay);
      };

      tryResolve();
    });
  }

  setStaticState() {
    this.root.classList.add("is-mounted", "is-static");

    if (this.backgroundImage.tagName === "IMG") {
      const initialImage = this.getImageForCurrentScreen();
      if (initialImage) this.backgroundImage.src = initialImage;
    }

    // Set button URL from data attribute or current slide
    let staticUrl =
      this.backgroundImage.dataset.activeCollectionUrl ||
      this.backgroundImage.getAttribute("data-active-collection-url");

    if (!staticUrl || staticUrl === "#") {
      staticUrl = this.currentSlide?.collectionUrl;
    }

    if (staticUrl && staticUrl !== "#") {
      this.button.setAttribute("href", staticUrl);
    }

    this.backgroundImage.style.opacity = "1";
    this.backgroundImage.style.transform = "scale(1)";
    this.button.style.opacity = "1";
    this.button.style.transform = "translateY(0)";
    this.thumbnails.forEach((thumb) => {
      thumb.style.opacity = "1";
      thumb.style.transform = "translateY(0)";
    });
  }

  init() {
    this.root.classList.add("is-mounted");

    // Set initial image src based on screen size (if using img tag)
    if (this.backgroundImage.tagName === "IMG") {
      const initialImage = this.getImageForCurrentScreen();
      if (initialImage) {
        this.backgroundImage.src = initialImage;
      }
    }

    // Set initial button URL from data attribute or current slide
    let initialUrl =
      this.backgroundImage.dataset.activeCollectionUrl ||
      this.backgroundImage.getAttribute("data-active-collection-url");

    if (!initialUrl || initialUrl === "#") {
      initialUrl = this.currentSlide?.collectionUrl;
    }

    if (initialUrl && initialUrl !== "#") {
      this.button.setAttribute("href", initialUrl);
    }

    this.gsap.set(this.backgroundImage, {
      scale: 1.05,
      opacity: this.currentSlide.image ? 0 : 1,
      transformOrigin: "center center",
    });

    // Only set initial state for thumbnails if they exist
    const elementsToAnimate = [this.button];
    if (this.thumbnails.length > 0) {
      elementsToAnimate.push(...this.thumbnails);
    }
    this.gsap.set(elementsToAnimate, {
      opacity: 0,
      y: 20,
    });

    this.gsap.set(this.header, { opacity: 0, y: -20 });

    this.buildIntroTimeline();

    // Only add event listeners if thumbnails exist
    if (this.thumbnails.length > 0) {
      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener("click", this.handleThumbnailClick);
      });
      this.setInteractionEnabled(true);
    }

    this.startAutoSlide();

    // Listen for window resize to switch between desktop/mobile images
    // Use debounce to avoid excessive calls during resize
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 150);
    });
  }

  handleResize() {
    if (!this.isAnimating) {
      const currentImage = this.getImageForCurrentScreen();
      if (currentImage && this.backgroundImage.tagName === "IMG") {
        if (this.backgroundImage.src !== currentImage) {
          this.backgroundImage.src = currentImage;
        }
      }
    }
  }

  buildIntroTimeline() {
    if (this.loadTimeline) {
      this.loadTimeline.kill();
    }

    this.loadTimeline = this.gsap.timeline({
      defaults: { ease: "power3.out" },
    });

    this.loadTimeline
      .to(this.backgroundImage, { opacity: 1, scale: 1, duration: 1.1 }, 0)
      .to(this.header, { opacity: 1, y: 0, duration: 0.4 }, 0.5)
      .to(this.button, { opacity: 1, y: 0, duration: 0.6 }, 0.35);

    // Only animate thumbnails if there are any
    if (this.thumbnails.length > 0) {
      this.loadTimeline.to(
        this.thumbnails,
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.12 },
        0.46
      );
    }
  }

  changeSlide(thumbnail) {
    const targetIndex = this.thumbnails.indexOf(thumbnail);
    if (targetIndex === -1 || this.isAnimating) {
      return;
    }
    const isMobile = window.innerWidth < 750;
    let desktopImage = thumbnail.dataset.image || "";
    let mobileImage = thumbnail.dataset.imageMobile || "";

    // Bidirectional fallback
    if (!desktopImage && mobileImage) desktopImage = mobileImage;
    if (!mobileImage && desktopImage) mobileImage = desktopImage;

    const nextSlide = {
      image: isMobile ? mobileImage : desktopImage,
      imageDesktop: desktopImage,
      imageMobile: mobileImage,
      thumb: thumbnail.dataset.thumbSrc || desktopImage || "",
      thumbSrcset: thumbnail.dataset.thumbSrcset || "",
      collectionUrl: thumbnail.dataset.collectionUrl || "#",
      alt: thumbnail.dataset.imageAlt || "",
    };

    if (!nextSlide.image) {
      return;
    }

    const previousSlide = { ...this.currentSlide };

    this.setInteractionEnabled(false);
    this.clearAutoSlideTimer();

    this.isAnimating = true;
    this.root.classList.add("is-animating");

    // Update button URL only if we have a valid collection URL
    if (nextSlide.collectionUrl && nextSlide.collectionUrl !== "#") {
      this.button.setAttribute("href", nextSlide.collectionUrl);
    }
    this.backgroundImage.setAttribute("aria-label", nextSlide.alt);

    const heroRect = this.root.getBoundingClientRect();
    // Pass desktop image for transition (will be converted to mobile if needed inside createTransitionLayer)
    const overlay = this.createTransitionLayer(
      thumbnail,
      nextSlide.imageDesktop || nextSlide.image,
      heroRect
    );
    const fadeTargets = [...this.thumbnails];

    if (this.sceneTimeline) {
      this.sceneTimeline.kill();
    }

    this.sceneTimeline = this.gsap.timeline({
      defaults: { ease: "power3.out" },
      onComplete: () => {
        if (overlay) {
          overlay.remove();
        }
        this.currentSlide = nextSlide;
        this.isAnimating = false;
        this.root.classList.remove("is-animating");

        // Ensure button URL is updated after animation completes (only if valid URL)
        if (
          this.button &&
          nextSlide.collectionUrl &&
          nextSlide.collectionUrl !== "#"
        ) {
          this.button.setAttribute("href", nextSlide.collectionUrl);
        }

        gsap.fromTo(
          fadeTargets,
          {
            opacity: 0,
            y: 30,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.55,
            stagger: (i) => (i === 0 ? 0 : 0.07),
          },
          "+=0.1"
        );
        this.activeIndex = targetIndex;
        this.currentThumbnailIndex = targetIndex;
        this.setInteractionEnabled(true);
        this.startAutoSlide();
      },
    });

    if (overlay) {
      this.sceneTimeline.to(
        overlay,
        {
          left: 0,
          top: 0,
          width: heroRect.width,
          height: heroRect.height,
          borderRadius: 0,
          duration: 0.75,
          ease: "power3.inOut",
        },
        0.05
      );

      this.sceneTimeline.add(() => {
        this.setBackgroundSlide(nextSlide);
      }, "-=0.05");
    } else {
      this.sceneTimeline.add(() => {
        this.setBackgroundSlide(nextSlide);
      }, 0.3);
    }

    this.sceneTimeline.add(() => {
      this.updateThumbnailWithSlide(thumbnail, previousSlide);
    }, "+=0");
  }

  setBackgroundSlide(slide) {
    let desktopImage = slide.imageDesktop || slide.image || "";
    let mobileImage = slide.imageMobile || "";

    // Bidirectional fallback
    if (!desktopImage && mobileImage) desktopImage = mobileImage;
    if (!mobileImage && desktopImage) mobileImage = desktopImage;

    const imageToUse = slide.image || this.getImageForCurrentScreen();

    if (imageToUse) {
      if (this.backgroundImage.tagName === "IMG") {
        this.backgroundImage.src = imageToUse;
        if (this.backgroundImage.alt !== slide.alt) {
          this.backgroundImage.alt = slide.alt || "";
        }
      } else {
        this.backgroundImage.style.backgroundImage = `url(${imageToUse})`;
        this.backgroundImage.setAttribute("aria-label", slide.alt || "");
      }

      this.backgroundImage.dataset.imageDesktop = desktopImage;
      this.backgroundImage.dataset.imageMobile = mobileImage;
    } else {
      if (this.backgroundImage.tagName === "IMG") {
        this.backgroundImage.src = "";
      } else {
        this.backgroundImage.style.removeProperty("background-image");
      }
      this.backgroundImage.dataset.imageDesktop = "";
      this.backgroundImage.dataset.imageMobile = "";
    }

    this.backgroundImage.dataset.activeThumbSrc = slide.thumb || "";
    this.backgroundImage.dataset.activeThumbSrcset = slide.thumbSrcset || "";
    this.backgroundImage.dataset.activeCollectionUrl =
      slide.collectionUrl || "#";

    // Update button URL to match the slide (only if valid URL)
    if (this.button && slide.collectionUrl && slide.collectionUrl !== "#") {
      this.button.setAttribute("href", slide.collectionUrl);
    }
  }

  updateThumbnailWithSlide(thumbnail, slide) {
    thumbnail.dataset.image = slide.imageDesktop || slide.image || "";
    thumbnail.dataset.imageMobile =
      slide.imageMobile || slide.imageDesktop || slide.image || "";
    thumbnail.dataset.imageAlt = slide.alt || "";
    thumbnail.dataset.collectionUrl = slide.collectionUrl || "#";
    thumbnail.dataset.thumbSrc = slide.thumb || slide.image || "";
    if (slide.thumbSrcset) {
      thumbnail.dataset.thumbSrcset = slide.thumbSrcset;
    } else if (thumbnail.dataset.thumbSrcset) {
      delete thumbnail.dataset.thumbSrcset;
    }

    const img = thumbnail.querySelector("[data-thumb-img]");
    if (!img) {
      return;
    }

    if (slide.thumb) {
      img.src = slide.thumb;
      if (slide.thumbSrcset) {
        img.srcset = slide.thumbSrcset;
      } else {
        img.removeAttribute("srcset");
      }
    } else if (slide.image) {
      img.src = slide.image;
      img.srcset = `${slide.image} 1x`;
    }

    img.alt = slide.alt || "";
  }

  createTransitionLayer(thumbnail, imageUrl, heroRect) {
    const isMobile = window.innerWidth < 750;
    let desktopImage = imageUrl || "";
    let mobileImage = thumbnail.dataset.imageMobile || "";

    // Bidirectional fallback
    if (!desktopImage && mobileImage) desktopImage = mobileImage;
    if (!mobileImage && desktopImage) mobileImage = desktopImage;

    const transitionImage = isMobile ? mobileImage : desktopImage;
    if (!transitionImage) return null;

    const overlay = document.createElement("div");
    overlay.className = "home-hero__transition-image";
    overlay.style.backgroundImage = `url(${transitionImage})`;

    const thumbImage = thumbnail.querySelector(".home-hero__thumbnail-image");
    const origin = thumbImage || thumbnail;
    const rect = origin.getBoundingClientRect();

    overlay.style.left = `${rect.left - heroRect.left}px`;
    overlay.style.top = `${rect.top - heroRect.top}px`;
    overlay.style.width = `${rect.width}px`;
    overlay.style.height = `${rect.height}px`;

    const computedRadius = window.getComputedStyle(origin).borderRadius;
    if (computedRadius) {
      overlay.style.borderRadius = computedRadius;
    }

    this.root.appendChild(overlay);

    return overlay;
  }

  extractImageUrl(value) {
    if (!value) return "";
    const match = value.match(/url\(["']?(.*?)["']?\)/i);
    return match ? match[1] : "";
  }

  handleThumbnailClick(event) {
    const thumbnail = event.currentTarget;
    if (!(thumbnail instanceof HTMLElement)) {
      return;
    }
    this.clearAutoSlideTimer();
    this.changeSlide(thumbnail);
  }

  handleAutoSlide() {
    if (!this.autoSlideEnabled) {
      return;
    }
    if (this.isAnimating) {
      this.startAutoSlide();
      return;
    }
    const nextThumbnail = this.getNextThumbnail();
    if (nextThumbnail) {
      // Don't animate if it's the same thumbnail (important for single block case)
      const nextIndex = this.thumbnails.indexOf(nextThumbnail);
      if (nextIndex !== -1 && nextIndex !== this.activeIndex) {
        this.changeSlide(nextThumbnail);
      } else {
        this.startAutoSlide();
      }
    } else {
      this.startAutoSlide();
    }
  }

  startAutoSlide() {
    if (!this.autoSlideEnabled) {
      return;
    }
    this.clearAutoSlideTimer();
    this.autoSlideTimer = window.setTimeout(
      this.handleAutoSlide,
      this.autoSlideInterval
    );
  }

  clearAutoSlideTimer() {
    if (!this.autoSlideTimer) {
      return;
    }
    window.clearTimeout(this.autoSlideTimer);
    this.autoSlideTimer = null;
  }

  resetAutoSlideTimer() {
    if (!this.autoSlideEnabled) {
      return;
    }
    this.clearAutoSlideTimer();
    this.startAutoSlide();
  }

  getNextThumbnail() {
    if (!this.thumbnails.length) {
      return null;
    }
    if (
      typeof this.currentThumbnailIndex !== "number" ||
      this.currentThumbnailIndex < 0 ||
      this.currentThumbnailIndex >= this.thumbnails.length
    ) {
      this.currentThumbnailIndex = 0;
    }
    const nextIndex = (this.currentThumbnailIndex + 1) % this.thumbnails.length;
    return this.thumbnails[nextIndex];
  }

  setInteractionEnabled(isEnabled) {
    // Only process if thumbnails exist
    if (this.thumbnails.length === 0) {
      return;
    }
    this.thumbnails.forEach((thumb) => {
      if (isEnabled) {
        thumb.style.pointerEvents = "";
        thumb.classList.remove("is-disabled");
        thumb.removeAttribute("aria-disabled");
      } else {
        thumb.style.pointerEvents = "none";
        thumb.classList.add("is-disabled");
        thumb.setAttribute("aria-disabled", "true");
      }
    });
  }
}

// ------------------PARALLAX (Reusable)-------------------------
class ParallaxImagePair {
  constructor(options) {
    this.container = options.container;
    this.leftImage = options.leftImage;
    this.rightImage = options.rightImage;
    this.intensity =
      typeof options.intensity === "number" ? options.intensity : 0.5;
    this.rotatableImages = Array.isArray(options.rotateTargets)
      ? options.rotateTargets
      : [];
    this.swapImages = Array.isArray(options.swapTargets)
      ? options.swapTargets
      : this.rotatableImages;
    this.swapIntervalMs =
      typeof options.swapInterval === "number" ? options.swapInterval : 4000;
    this.swapDuration =
      typeof options.swapDuration === "number" ? options.swapDuration : 450;
    this.animationFrame = null;
    this.swapTimer = null;
    this.isSwapping = false;

    if (!this.container || !this.leftImage || !this.rightImage) return;
    if (window.innerWidth <= 999) return; // desktop only

    this.leftImage.style.willChange = "transform";
    this.rightImage.style.willChange = "transform";

    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.swapRandomImages = this.swapRandomImages.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    this.container.addEventListener("mousemove", this.handleMouseMove);
    this.container.addEventListener("mouseleave", this.handleMouseLeave);

    this.rotatableImages.forEach((img) => {
      img.style.setProperty("--flowers-rotate", "0deg");
      img.style.setProperty("--flowers-scale", "1");
      img.style.opacity = "1";
    });

    if (this.swapImages.length >= 2) {
      this.startSwapTimer();
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
    }
  }

  handleMouseMove(e) {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    this.animationFrame = requestAnimationFrame(() => {
      const rect = this.container.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const mouseX = (e.clientX - centerX) / (rect.width / 2);
      const mouseY = (e.clientY - centerY) / (rect.height / 2);

      const moveX = mouseX * this.intensity * 100;
      const moveY = mouseY * this.intensity * 100;

      this.leftImage.style.transform = `translate(${-moveX}px, ${-moveY}px)`;
      this.rightImage.style.transform = `translate(${moveX}px, ${moveY}px)`;

      if (this.rotatableImages.length) {
        this.updateImageRotations(e);
      }
    });
  }

  handleMouseLeave() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    this.leftImage.style.transition = "transform 0.6s ease-out";
    this.rightImage.style.transition = "transform 0.6s ease-out";

    this.leftImage.style.transform = "translate(0, 0)";
    this.rightImage.style.transform = "translate(0, 0)";

    setTimeout(() => {
      this.leftImage.style.transition = "";
      this.rightImage.style.transition = "";
    }, 600);

    this.rotatableImages.forEach((img) => {
      img.style.setProperty("--flowers-rotate", "0deg");
    });
  }

  updateImageRotations(event) {
    const maxAngle = 24;
    this.rotatableImages.forEach((img) => {
      const rect = img.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const angleRad = Math.atan2(
        event.clientY - centerY,
        event.clientX - centerX
      );
      const angleDeg = (angleRad * 180) / Math.PI;
      const adjusted = Math.max(Math.min(angleDeg * 0.45, maxAngle), -maxAngle);
      img.style.setProperty("--flowers-rotate", `${adjusted}deg`);
    });
  }

  startSwapTimer() {
    if (this.swapTimer || this.swapImages.length < 2) return;
    this.swapTimer = window.setInterval(
      this.swapRandomImages,
      this.swapIntervalMs
    );
  }

  stopSwapTimer() {
    if (!this.swapTimer) return;
    window.clearInterval(this.swapTimer);
    this.swapTimer = null;
  }

  handleVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this.stopSwapTimer();
    } else {
      this.startSwapTimer();
    }
  }

  animateImageState(img, scale, opacity) {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        img.style.setProperty("--flowers-scale", String(scale));
        if (typeof opacity === "number") {
          img.style.opacity = String(opacity);
        }
      });
      window.setTimeout(resolve, this.swapDuration);
    });
  }

  swapImageSources(imgA, imgB) {
    const srcA = imgA.getAttribute("src");
    const srcsetA = imgA.getAttribute("srcset");
    const altA = imgA.getAttribute("alt");
    const indexA = imgA.getAttribute("data-index");

    const srcB = imgB.getAttribute("src");
    const srcsetB = imgB.getAttribute("srcset");
    const altB = imgB.getAttribute("alt");
    const indexB = imgB.getAttribute("data-index");

    if (srcA !== null) {
      imgB.setAttribute("src", srcA);
    } else {
      imgB.removeAttribute("src");
    }
    if (srcsetA) {
      imgB.setAttribute("srcset", srcsetA);
    } else {
      imgB.removeAttribute("srcset");
    }
    if (altA !== null) {
      imgB.setAttribute("alt", altA);
    }
    if (indexA !== null) {
      imgB.setAttribute("data-index", indexA);
    } else {
      imgB.removeAttribute("data-index");
    }

    if (srcB !== null) {
      imgA.setAttribute("src", srcB);
    } else {
      imgA.removeAttribute("src");
    }
    if (srcsetB) {
      imgA.setAttribute("srcset", srcsetB);
    } else {
      imgA.removeAttribute("srcset");
    }
    if (altB !== null) {
      imgA.setAttribute("alt", altB);
    }
    if (indexB !== null) {
      imgA.setAttribute("data-index", indexB);
    } else {
      imgA.removeAttribute("data-index");
    }
  }

  swapRandomImages() {
    if (this.isSwapping || this.swapImages.length < 2) return;
    const pool = this.swapImages.filter((img) => img.isConnected);
    if (pool.length < 2) return;

    const firstIndex = Math.floor(Math.random() * pool.length);
    let secondIndex = firstIndex;
    while (secondIndex === firstIndex) {
      secondIndex = Math.floor(Math.random() * pool.length);
    }

    const imgA = pool[firstIndex];
    const imgB = pool[secondIndex];

    this.isSwapping = true;

    Promise.all([
      this.animateImageState(imgA, 0.4, 0),
      this.animateImageState(imgB, 0.4, 0),
    ])
      .then(() => {
        this.swapImageSources(imgA, imgB);
        return Promise.all([
          this.animateImageState(imgA, 1, 1),
          this.animateImageState(imgB, 1, 1),
        ]);
      })
      .catch(() => {})
      .finally(() => {
        this.isSwapping = false;
      });
  }
}

const initParallaxPair = ({
  containerSelector,
  leftSelector,
  rightSelector,
  intensity,
  rotateSelector,
  swapSelector,
  swapInterval,
  swapDuration,
}) => {
  const container = document.querySelector(containerSelector);
  const leftImage = container ? container.querySelector(leftSelector) : null;
  const rightImage = container ? container.querySelector(rightSelector) : null;
  if (!container || !leftImage || !rightImage) return null;
  const rotateTargets =
    container && rotateSelector
      ? Array.from(container.querySelectorAll(rotateSelector))
      : [];
  const swapTargets =
    container && swapSelector
      ? Array.from(container.querySelectorAll(swapSelector))
      : rotateTargets;
  return new ParallaxImagePair({
    container,
    leftImage,
    rightImage,
    intensity,
    rotateTargets,
    swapTargets,
    swapInterval,
    swapDuration,
  });
};
